import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import { useDebounce } from "@app/util/use-debounce";
import { TextFieldComponent, ClickableComponent } from "@app/core";
import Plus from "@assets/icons/rounded/add.svg";
import Search from "@assets/icons/rounded/search.svg";
import { SelectComponent } from "@app/core/select";
import { ModalComponent } from "@app/core/modal";
import { getTranslatedPlatforms } from "@app/constants/platform";
import { getTranslatedBannerStatus, getTranslatedBannerTypes } from "@app/constants/banner";
import { useAppDispatch, useAppSelector } from "@app/redux/store";
import { setCurrentPlatform, setCurrentSearch, setCurrentStatus, setCurrentType } from "@app/redux/reducers/banners";

import { CreateBannerModal } from "../create-banner-modal";
import styles from "./banner-filter-bar-component.module.scss";

const BannerFilterBar = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { currentSearch, currentPlatform, currentStatus, currentType } = useAppSelector((state) => state.banners);

  const [search, setSearch] = useState<string>(currentSearch || "");
  const [isAddBannerOpen, setIsAddBannerOpen] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(search, 500);

  const translatedPlatforms = getTranslatedPlatforms(intl);
  const translatedBannerStatus = getTranslatedBannerStatus(intl);
  const translatedBannerTypes = getTranslatedBannerTypes(intl);

  useEffect(() => {
    dispatch(setCurrentSearch(debouncedSearchTerm || undefined));
  }, [debouncedSearchTerm]);

  const onStatusChange = (e: React.ChangeEvent<any>) => {
    const value = Number(e.target.value);
    dispatch(setCurrentStatus(value || undefined));
  };

  const onPlatformChange = (e: React.ChangeEvent<any>) => {
    const value = Number(e.target.value);
    dispatch(setCurrentPlatform(value || undefined));
  };

  const onTypeChange = (e: React.ChangeEvent<any>) => {
    dispatch(setCurrentType(e.target.value === "all" ? undefined : e.target.value));
  };

  return (
    <div className={styles.bannerFilterBar}>
      <div className={styles.searchField}>
        <TextFieldComponent
          height={40}
          placeholder={intl.formatMessage({ id: "bannerFilterBar.input.search.placeholder" })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          type="text"
          id="search"
          icon={Search}
          width={318}
        />
      </div>

      <SelectComponent onChange={onStatusChange} value={currentStatus} options={[...translatedBannerStatus]} />

      <SelectComponent
        onChange={onPlatformChange}
        value={currentPlatform}
        options={[
          { id: 0, name: intl.formatMessage({ id: "bannerFilterBar.input.platform.all" }) },
          ...translatedPlatforms
        ]}
      />

      <SelectComponent
        onChange={onTypeChange}
        value={currentType}
        options={[
          { id: "all", name: intl.formatMessage({ id: "bannerFilterBar.input.types.all" }) },
          ...translatedBannerTypes
        ]}
      />

      <ClickableComponent
        title={intl.formatMessage({ id: "bannerFilterBar.button.addBanner" })}
        height={40}
        icon={Plus}
        iconSize="20px"
        iconFillColor="#ffffff"
        iconPosition="left"
        onClick={() => setIsAddBannerOpen(true)}
      />

      <ModalComponent
        title={intl.formatMessage({ id: "bannerFilterBar.button.addBanner" })}
        isModalOpen={isAddBannerOpen}
        onCloseModal={() => {
          setIsAddBannerOpen(false);
        }}
        variant="big"
      >
        <CreateBannerModal onCancel={() => setIsAddBannerOpen(false)} />
      </ModalComponent>
    </div>
  );
};

export { BannerFilterBar };
